import React, { useEffect } from 'react';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/kachng-digital-receipt/id6502332854?itscg=30200&itsct=apps_box_badge&mttnsubad=6502332854';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.Gibbas.KACHNG';

export function detectMobileStore() {
  if (typeof navigator === 'undefined') return null;

  const userAgent = navigator.userAgent || '';
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  const isAndroid = /Android/i.test(userAgent) || /Android/i.test(platform);
  const isIOS =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    /iPhone|iPad|iPod/i.test(platform) ||
    (platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isAndroid) return 'android';
  if (isIOS) return 'ios';
  return null;
}

export default function Download() {
  useEffect(() => {
    document.title = 'Download KACHNG for iPhone and Android';

    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    description.setAttribute(
      'content',
      'Download KACHNG and turn everyday receipts into spending intelligence. Available on the Apple App Store and Google Play.'
    );

    const store = detectMobileStore();
    if (store === 'ios') window.location.replace(APP_STORE_URL);
    if (store === 'android') window.location.replace(PLAY_STORE_URL);
  }, []);

  return (
    <main style={styles.page}>
      <style>{`
        .kachng-store-grid {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .kachng-store-button {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          box-sizing: border-box;
          width: max-content;
          padding: 16px 32px;
          border: 0;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 700;
          white-space: nowrap;
          text-decoration: none;
          transition: transform 160ms ease, background 160ms ease,
            box-shadow 160ms ease;
        }

        .kachng-store-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(50, 232, 117, 0.16);
        }

        .kachng-store-button--ios {
          background: var(--kachng-green);
          color: var(--bg-ink);
        }

        .kachng-store-button--ios:hover {
          box-shadow: 0 10px 30px rgba(0, 230, 118, 0.26);
        }

        .kachng-store-button--android {
          background: var(--green-accent-bg);
          color: var(--kachng-green);
        }

        .kachng-store-button--android:hover {
          background: var(--green-accent-bg-hi);
        }

        .kachng-store-button:focus-visible,
        .kachng-home-link:focus-visible {
          outline: 3px solid var(--kachng-green);
          outline-offset: 4px;
        }

        .kachng-home-link {
          margin-top: 30px;
          color: #b8b8b8;
          font-size: 14px;
          text-decoration: none;
        }

        .kachng-home-link:hover {
          color: #32e875;
        }

        @media (max-width: 620px) {
          .kachng-store-grid {
            flex-direction: column;
            align-items: stretch;
            width: min(100%, 320px);
          }

          .kachng-store-button {
            width: 100%;
          }
        }
      `}</style>

      <section style={styles.card}>
        <a href="/" aria-label="Return to KACHNG homepage" style={styles.logo}>
          KACHNG
        </a>

        <div aria-hidden="true" style={styles.eyebrow}>
          AVAILABLE NOW
        </div>

        <h1 style={styles.heading}>Your money has a memory now.</h1>
        <p style={styles.copy}>
          Download KACHNG and turn everyday receipts into spending intelligence.
        </p>

        <div className="kachng-store-grid" aria-label="Choose your app store">
          <a
            className="kachng-store-button kachng-store-button--ios"
            href={APP_STORE_URL}
            aria-label="Download KACHNG on the Apple App Store"
          >
            Download for iOS
          </a>

          <a
            className="kachng-store-button kachng-store-button--android"
            href={PLAY_STORE_URL}
            aria-label="Get KACHNG on Google Play"
          >
            Download for Android
          </a>
        </div>

        <a className="kachng-home-link" href="/">
          ← Back to KACHNG
        </a>
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: 'clamp(24px, 6vw, 72px)',
    background:
      'radial-gradient(circle at 50% 18%, rgba(50, 232, 117, 0.13), transparent 34%), #050505',
    color: '#ffffff',
    fontFamily: 'inherit',
  },
  card: {
    width: '100%',
    maxWidth: '760px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  logo: {
    marginBottom: '56px',
    color: '#ffffff',
    fontSize: 'clamp(48px, 8vw, 64px)',
    lineHeight: 1.06,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    textDecoration: 'none',
  },
  eyebrow: {
    marginBottom: '18px',
    color: '#32e875',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.2em',
  },
  heading: {
    maxWidth: '720px',
    margin: '0 0 18px',
    fontSize: 'clamp(42px, 7vw, 76px)',
    lineHeight: 0.98,
    letterSpacing: '-0.055em',
  },
  copy: {
    maxWidth: '570px',
    margin: '0 0 38px',
    color: '#b8b8b8',
    fontSize: 'clamp(17px, 2.3vw, 21px)',
    lineHeight: 1.55,
  },
};
